import { useState } from 'react'
import { PlusCircleIcon } from 'lucide-react'
import { Button } from '@/shared/ui/Button/Button'
import { useTranslation } from 'react-i18next'
import { CreateWebinarModal } from './Modals/CreateWebinarModal'
import { EditWebinarModal } from './Modals/EditWebinarModal'
import { WebinarsTable } from './WebinarsTable/WebinarsTable'
import { DeleteWebinarAlert } from '@/features/Webinar/DeleteWebinarAlert'
import { getCurrentTranslation } from '@/shared/lib/utils'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import { FullTextDescriptionModal } from '@/shared/ui/ModalDialogs/FullTextDescriptionModal'
import { Webinar } from '@/shared/api/topli/TopliApi'
import { ShowVideoModal } from '@/shared/ui/ModalDialogs/ShowVideoModal'

export const WebinarsView = () => {
  const [currentTable, setCurrentTable] = useState<Webinar | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [showMoreDialogOpen, setShowMoreDialogOpen] = useState(false)
  const [showVedeoDialogOpen, setShowVideoDialogOpen] = useState(false)
  const { t } = useTranslation(['webinar'])
  const { language } = useLanguageStore()

  const handleCloseDialog = () => {
    setCurrentTable(null)
    setCreateDialogOpen(false)
    setEditDialogOpen(false)
    setDeleteDialogOpen(false)
  }

  const handleOpenEditModal = (selectedWebinar: Webinar) => {
    setCurrentTable(selectedWebinar)
    setEditDialogOpen(true)
  }

  const handleOpenDeleteModal = (selectedWebinar: Webinar) => {
    setCurrentTable(selectedWebinar)
    setDeleteDialogOpen(true)
  }

  const handleOpenShowMoreModal = (webinar: Webinar) => {
    const translatedDescription = getCurrentTranslation(webinar, language, 'description')
    setCurrentTable({
      ...webinar,
      description: translatedDescription,
    })
    setShowMoreDialogOpen(true)
  }

  const handleOpenShowVedeo = (selectedWebinar: Webinar) => {
    setCurrentTable(selectedWebinar)
    setShowVideoDialogOpen(true)
  }

  return (
    <div className='flex flex-col md:w-full w-[calc(100%+2.5rem)] md:mx-0 mx-[-1.25rem] bg-white rounded-[1.25rem] shadow-navbar p-4'>
      <WebinarsTable
        onOpenEditModal={handleOpenEditModal}
        onOpenDeleteModal={handleOpenDeleteModal}
        onOpenFullDescription={handleOpenShowMoreModal}
        onOpenShowVideo={handleOpenShowVedeo}
        addButtonSlot={
          <Button
            variant='primary'
            color='base'
            size='md'
            onClick={() => setCreateDialogOpen(true)}
            leftIcon={<PlusCircleIcon size={16} />}
          >
            {t('webinar:buttons.new_webinar')}
          </Button>
        }
      />
      <CreateWebinarModal
        dialogProps={{ open: createDialogOpen, onOpenChange: setCreateDialogOpen }}
        onCloseModal={handleCloseDialog}
      />
      {currentTable !== null && (
        <EditWebinarModal
          dialogProps={{ open: editDialogOpen, onOpenChange: setEditDialogOpen }}
          currentWebinarId={currentTable.id}
          onCloseModal={handleCloseDialog}
        />
      )}
      {currentTable !== null && (
        <DeleteWebinarAlert
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          selectWebinarId={currentTable.id}
          onClose={handleCloseDialog}
        />
      )}
      {currentTable && (
        <FullTextDescriptionModal
          dialogProps={{ open: showMoreDialogOpen, onOpenChange: setShowMoreDialogOpen }}
          description={currentTable?.description}
        />
      )}
      {currentTable && (
        <ShowVideoModal
          dialogProps={{ open: showVedeoDialogOpen, onOpenChange: setShowVideoDialogOpen }}
          videoUrl={currentTable?.video_url}
        />
      )}
    </div>
  )
}
